import date from 'date-and-time';
import { SerializedReel, SerializedScheduledPost, SerializedSocialChannel } from '../promote/types';

/** How many next posts and latest reels the Promote block lists (design a1). */
export const PROMOTE_LIST_ROWS = 3;

/** How far back a failed post still asks for attention, as the Calendar badge does. */
const FAILED_LOOKBACK_DAYS = 30;

/** How far ahead the block reads scheduled posts. */
const SCHEDULE_LOOKAHEAD_DAYS = 31;

/** The statuses of a post that is still on its way out. */
const UPCOMING_STATUSES: ReadonlySet<SerializedScheduledPost['status']> = new Set([
  'scheduled',
  'rendering',
  'ready',
  'awaitingManualPost',
  'publishing',
]);

/**
 * The one posts window of the block: 30 days back for failed posts, 31 days ahead for the next ones.
 * It starts on a local midnight so the query key does not change on every render.
 */
export function promotePostsWindow(now: Date): { from: string; to: string } {
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return {
    from: date.addDays(startOfToday, -FAILED_LOOKBACK_DAYS).toISOString(),
    to: date.addDays(startOfToday, SCHEDULE_LOOKAHEAD_DAYS).toISOString(),
  };
}

/** The channels the platform dropped: `disconnected` while the connection is still known. */
export function channelsNeedingReconnect(channels: SerializedSocialChannel[]): SerializedSocialChannel[] {
  return channels.filter((channel) => channel.status === 'disconnected' && channel.connection !== null);
}

/** True when the artist connected at least one channel, by sign-in or by handle. */
export function hasAnyChannel(channels: SerializedSocialChannel[]): boolean {
  return channels.some(
    (channel) => channel.connection !== null || channel.status === 'connected' || channel.handle !== null
  );
}

/** The next posts still to go out, soonest first. */
export function upcomingPosts(posts: SerializedScheduledPost[], now: Date): SerializedScheduledPost[] {
  return posts
    .filter((post) => UPCOMING_STATUSES.has(post.status) && Date.parse(post.scheduledAt) >= now.getTime())
    .sort((a, b) => Date.parse(a.scheduledAt) - Date.parse(b.scheduledAt));
}

export function failedPosts(posts: SerializedScheduledPost[]): SerializedScheduledPost[] {
  return posts.filter((post) => post.status === 'failed');
}

/** The newest reels first. */
export function latestReels(reels: SerializedReel[], limit: number = PROMOTE_LIST_ROWS): SerializedReel[] {
  return [...reels].sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt)).slice(0, limit);
}
